import { css } from '@emotion/css';
import { antdTokenFilter } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { Button, Checkbox, Flex, Select, Switch, Table } from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import type React from 'react';
import { useState } from 'react';
import type { InsuranceDefinitionInput } from '../graphql/generated/graphql.ts';

const { Option } = Select;

type Insurance = { id: string; shortName: string };
type Diagnose = { id: string; name: string };
type PeriodicGroup = { id: string; name: string };

export const ParameterInsuranceEditor: React.FC<{
  insurances: readonly Insurance[];
  diagnoses: readonly Diagnose[];
  periodicGroups: readonly PeriodicGroup[];
  lg: boolean;
}> = ({ insurances, diagnoses, lg, periodicGroups }) => {
  const form = useFormInstance();

  // we need to use a dedicated state here as the React compiler does not like the use of form.getFieldValue('insuranceDefinitions')
  // directly.
  // The compiler would optimize it away, and useWatch doesn't work with subcomponents
  const [values, setValues] = useState<InsuranceDefinitionInput[]>(
    (form.getFieldValue('insuranceDefinitions') as InsuranceDefinitionInput[])?.filter(it => it.lg === lg) ?? []
  );

  const syncValuesState = (values: InsuranceDefinitionInput[]) => {
    setValues(values);
    form.setFieldValue('insuranceDefinitions', [
      ...((form.getFieldValue('insuranceDefinitions') as InsuranceDefinitionInput[])?.filter(it => it.lg !== lg) ?? []),
      ...values,
    ]);
  };

  const onChange = (selectedRows: Insurance[]) => {
    syncValuesState(
      selectedRows.map(row => {
        const entry = values.find(it => it.insuranceId === row.id);
        return {
          insuranceId: row.id,
          diagnoseIds: entry?.diagnoseIds ?? [],
          lg: lg,
          periodicGroupId: entry?.periodicGroupId ?? null,
          deductible: entry?.deductible ?? false,
        };
      })
    );
  };

  const onChangeDiagnoses = (insurance: Insurance, diagnoseIds: string[]) => {
    const entry = values.find(it => it.insuranceId === insurance.id);
    syncValuesState([
      ...values.filter(it => it.insuranceId !== insurance.id),
      {
        insuranceId: insurance.id,
        diagnoseIds: diagnoseIds,
        lg: lg,
        periodicGroupId: entry?.periodicGroupId ?? null,
        deductible: entry?.deductible ?? false,
      },
    ]);
  };

  const onChangePeriodicGroup = (insurance: Insurance, periodicGroupId: string | null) => {
    const entry = values.find(it => it.insuranceId === insurance.id);
    syncValuesState([
      ...values.filter(it => it.insuranceId !== insurance.id),
      {
        insuranceId: insurance.id,
        diagnoseIds: entry?.diagnoseIds ?? [],
        lg: lg,
        periodicGroupId: periodicGroupId,
        deductible: entry?.deductible ?? false,
      },
    ]);
  };

  const onChangeDeductible = (insurance: Insurance, deductible: boolean) => {
    const entry = values.find(it => it.insuranceId === insurance.id);
    syncValuesState([
      ...values.filter(it => it.insuranceId !== insurance.id),
      {
        insuranceId: insurance.id,
        diagnoseIds: entry?.diagnoseIds ?? [],
        lg: lg,
        periodicGroupId: entry?.periodicGroupId ?? null,
        deductible: deductible,
      },
    ]);
  };

  const populateAllDiagnoses = (diagnoseIds: string[]) => {
    syncValuesState(values.map(it => ({ ...it, diagnoseIds: diagnoseIds })));
  };

  const populateAllPeriodicGroups = (periodicGroupId: string | null) => {
    syncValuesState(values.map(it => ({ ...it, periodicGroupId: periodicGroupId })));
  };

  const populateAllDeductibles = (deductible: boolean) => {
    syncValuesState(values.map(it => ({ ...it, deductible: deductible })));
  };

  return (
    <Table<Insurance>
      rowKey={insurance => insurance.id}
      size="small"
      showSorterTooltip={false}
      dataSource={insurances}
      pagination={false}
      rowSelection={{
        type: 'checkbox',
        selectedRowKeys: values.map(it => it.insuranceId),
        onChange: (_, selectedRows) => onChange(selectedRows),
      }}
      columns={[
        {
          title: 'Name',
          dataIndex: 'shortName',
          key: 'shortName',
          ellipsis: true,
          width: 150,
        },
        {
          title: 'Diagnosen',
          key: 'diagnoses',
          render: (_, record) => {
            const entry = values.find(it => it.insuranceId === record.id);
            return (
              <Flex justify="center" align="center">
                <Select
                  disabled={!entry}
                  value={entry?.diagnoseIds ?? []}
                  onChange={value => onChangeDiagnoses(record, value)}
                  className={css`
                    flex-grow: 1;
                    width: 70%;
                  `}
                  showSearch={{ filterOption: antdTokenFilter }}
                  allowClear
                  mode="multiple"
                  placeholder="Bitte auswählen..."
                  popupMatchSelectWidth={false}
                >
                  {[...diagnoses]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(diagnose => (
                      <Option key={diagnose.id} value={diagnose.id} search={diagnose.name}>
                        {diagnose.name}
                      </Option>
                    ))}
                </Select>
                <Button type="link" disabled={!entry} onClick={() => populateAllDiagnoses(entry?.diagnoseIds ?? [])}>
                  Für alle
                </Button>
              </Flex>
            );
          },
        },
        {
          title: 'Periodische Verrechnungsgruppe',
          key: 'periodicGroup',
          render: (_, record) => {
            const entry = values.find(it => it.insuranceId === record.id);
            return (
              <Flex justify="center" align="center">
                <Select
                  disabled={!entry}
                  value={entry?.periodicGroupId}
                  onChange={value => onChangePeriodicGroup(record, value == null ? null : String(value))}
                  className={css`
                    flex-grow: 1;
                    width: 70%;
                  `}
                  showSearch={{ filterOption: antdTokenFilter }}
                  allowClear
                  placeholder="Bitte auswählen..."
                  popupMatchSelectWidth={false}
                >
                  {[...periodicGroups]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(group => (
                      <Option key={group.id} value={group.id} search={group.name}>
                        {group.name}
                      </Option>
                    ))}
                </Select>
                <Button
                  type="link"
                  disabled={!entry}
                  onClick={() => populateAllPeriodicGroups(entry?.periodicGroupId == null ? null : String(entry.periodicGroupId))}
                >
                  Für alle
                </Button>
              </Flex>
            );
          },
        },
        {
          title: () => {
            const allChecked = !!values.length && values.every(it => it.deductible);
            return (
              <div>
                SB{' '}
                <Checkbox
                  checked={allChecked}
                  indeterminate={values.some(it => it.deductible) && !allChecked}
                  onChange={e => populateAllDeductibles(e.target.checked)}
                />
              </div>
            );
          },
          key: 'deductible',
          width: 65,
          render: (_, record) => {
            const entry = values.find(it => it.insuranceId === record.id);
            return (
              <Flex justify="center" align="center">
                <Switch size="small" disabled={!entry} checked={entry?.deductible} onChange={e => onChangeDeductible(record, e)} />
              </Flex>
            );
          },
        },
      ]}
    />
  );
};
